import React, { memo } from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { FileDoneOutlined, FileExclamationOutlined } from "@ant-design/icons"
import { WAIVER_STATUS } from "pagesV2/pro/Calendar/shared/bookingTileUtils"
import styles from "./index.module.css"

const ICONS = {
  [WAIVER_STATUS.SIGNED]: <FileDoneOutlined />,
  [WAIVER_STATUS.PENDING]: <FileExclamationOutlined />
}

/**
 * Waiver status chip for calendar booking tiles.
 *
 * The design reduces this to an icon and a number on a tinted pill — red for outstanding waivers,
 * green once they are all in — so it can sit beside the capacity counter on a tile that only has
 * room for one short row. The wording it replaced ("2 pending") lives on in `label`, which is the
 * chip's tooltip and its accessible name.
 *
 * The colour is the status's, not the booking's palette: a waiver still to sign has to read the
 * same on every experience colour.
 */
const WaiverBadge = ({ status = WAIVER_STATUS.PENDING, label = "", count = 0 }) => {
  const isSigned = status === WAIVER_STATUS.SIGNED
  return (
    <Flex
      align="center"
      gap={4}
      className={`${styles.badge} ${isSigned ? styles.badgeSigned : styles.badgePending}`}
      title={label}
      aria-label={label}
    >
      <span className={styles.icon} aria-hidden="true">
        {ICONS[status] || ICONS[WAIVER_STATUS.PENDING]}
      </span>
      {count > 0 && <span className={styles.count}>{count}</span>}
    </Flex>
  )
}

WaiverBadge.propTypes = {
  status: PropTypes.oneOf([WAIVER_STATUS.SIGNED, WAIVER_STATUS.PENDING]),
  /** Full wording — "Signed" / "2 pending" — kept as the tooltip and accessible name. */
  label: PropTypes.string,
  /** Guests still to sign; hidden when zero, which is the signed chip. */
  count: PropTypes.number
}

export default memo(WaiverBadge)
